/**
 * shared/space_survey.ts — CLIENT MIRROR of server/src/lib/space_survey.ts (the server copy is authoritative; keep
 * them byte-identical below the headers — verify:space-survey diffs them). Space campaign slice F, 2026-09-30:
 * discovery that makes work — the content version, the lease, server-metered work, the survey desks, intel licences,
 * the expedition log, the mystery's public numbers. Pure.
 */
// ── THE CONTENT VERSION ─────────────────────────────────────────────────────
// A site generation seeded while `discovery_jobs_v1` is on carries version 2: shared stock, a lease, a hazard and
// server-metered work. A generation seeded before (or with the flag off) has NO survey row and keeps the per-pilot
// recovery it was promised (the report §20.3: never silently turn a personal recovery into a race).
export const DISCOVERY_FLAG = 'discovery_jobs_v1';
export const CONTENT_V2 = 2;

/** What a hashed signature kind becomes under version 2. */
export type SiteKind = 'relay' | 'component' | 'vault';
export function v2KindOf(sigKind: string): SiteKind | null {
  if (sigKind === 'cache') return 'relay';
  if (sigKind === 'derelict') return 'component';
  if (sigKind === 'relic') return 'vault';
  return null;
}
export const SITE_LABEL: Record<SiteKind, string> = {
  relay: 'drifting survey relay',
  component: 'derelict with one intact component',
  vault: 'relic vault',
};
export type SiteState = 'available' | 'leased' | 'depleted' | 'collapsed' | 'expired';
export type SiteStage = 'observed' | 'reported' | 'assessed';

/** A generation stays recoverable to the end of the FOLLOWING epoch (today's "current or previous" eligibility). */
export const EPOCH_MS = 4 * 3_600_000;
export function recoverUntilMs(epoch: number): number { return (epoch + 2) * EPOCH_MS; }

// ── STOCK + HAZARD (seeded on the shard's secret salt: system + generation + version) ──
export const RELAY_UNITS = 2;                 // a relay rides as a 2 u lot
export const RELAY_FAMILY = 'survey_relay';    // not a resource family: it never sells, never lands
export const RELAY_TAG_PREFIX = 'x:';          // x:<sigKey> — bound for a research post
export const VAULT_BASE_U = 18;                // a vault holds 3 × (6 + 4·tier)
export const VAULT_PER_TIER_U = 12;
export const VAULT_BATCH_MAX = 24;             // one lease takes at most this many units
export const VAULT_STRAIN_PER_U = 2;           // every unit pulled strains the vault
export const DAMAGE_AT = 50;                   // a relay / component pulled at hazard ≥ this comes out damaged
export const DAMAGED_PAY_PCT = 60;             // a damaged relay earns this share of its tender
export const DAMAGED_QUALITY_PCT = 65;         // a damaged component keeps this share of its quality

export function vaultStock(tier: number): number { return VAULT_BASE_U + VAULT_PER_TIER_U * Math.max(1, Math.min(3, tier)); }
/** The seeded numbers of one site from three uniform draws in [0, 1). */
export function seedSite(kind: SiteKind, tier: number, h1: number, h2: number, h3: number): { stock: number; hazard: number; collapseAt: number; quality: number } {
  const hazard = 20 + Math.floor(h1 * 66);                 // 20..85
  const collapseAt = 70 + Math.floor(h2 * 26);              // 70..95
  const quality = 300 + 120 * Math.max(1, Math.min(3, tier)) + Math.floor(h3 * 250);
  const stock = kind === 'vault' ? vaultStock(tier) : 1;
  return { stock, hazard, collapseAt, quality };
}
/** The coarse band a hazard report carries: 'high' means a pull now comes out damaged. */
export function hazardBand(h: number): 'low' | 'medium' | 'high' { return h < 35 ? 'low' : h < DAMAGE_AT ? 'medium' : 'high'; }

// ── THE LEASE ───────────────────────────────────────────────────────────────
export const LEASE_S = 1_800;                  // 30 min, capped by the site's recover-until
export const LEASE_GRACE_S = 120;              // a claimant offline this long keeps the work, loses exclusivity
export const LEASE_BOND_SOV_PER_TIER = 25;     // with no funded tender, a lease posts a bond
export const MAX_WORKED_SITES_PER_SYSTEM = 3;  // at most three sites a system are leased at once
export function leaseExpiresMs(nowMs: number, recoverMs: number): number { return Math.min(nowMs + LEASE_S * 1000, recoverMs); }
export function leaseBondSov(tier: number): number { return LEASE_BOND_SOV_PER_TIER * Math.max(1, Math.min(3, tier)); }
/** Units a lease reserves: a relay its 2 u, a component 0 (it rides in the inventory), a vault what the hold takes. */
export function reserveUnits(kind: SiteKind, stock: number, holdFree: number): number {
  if (kind === 'relay') return RELAY_UNITS;
  if (kind === 'component') return 0;
  return Math.max(0, Math.min(stock, holdFree, VAULT_BATCH_MAX));
}

export interface LeaseFacts {
  kind: SiteKind | null; state: SiteState; stock: number; round: number;
  nowMs: number; recoverMs: number; hasOtherLease: boolean; workedHere: number;
  holdFree: number; funded: boolean; sameSystem: boolean;
  /** The current lease on the site, if any. */
  leasedByMe: boolean; leaseTakeable: boolean;
}
/** Why a pilot cannot lease a site right now ('' = they can). */
export function leaseProblem(f: LeaseFacts): string {
  if (!f.kind) return 'That site keeps the old rules — recover it with the beam';
  if (!f.sameSystem) return 'The site is in another system';
  if (f.round < 3) return 'Resolve the site first — three scanner rounds';
  if (f.nowMs >= f.recoverMs || f.state === 'expired') return 'That site has faded';
  if (f.state === 'depleted' || f.state === 'collapsed' || f.stock <= 0) return 'Nothing is left to recover there';
  if (f.leasedByMe) return 'You already hold this lease';
  if (f.state === 'leased' && !f.leaseTakeable) return 'Another pilot holds the lease — it frees when they finish, leave or let it lapse';
  if (f.hasOtherLease) return 'You already hold a lease — finish or release it first';
  if (f.workedHere >= MAX_WORKED_SITES_PER_SYSTEM) return `Three sites here are already being worked — try again when one frees`;
  const need = f.kind === 'relay' ? RELAY_UNITS : f.kind === 'vault' ? 1 : 0;
  if (f.holdFree < need) return `Hold full — the lease needs ${need} u free`;
  if (!f.funded) return 'You cannot cover the lease bond';
  return '';
}

// ── SERVER-METERED WORK (the report §21.3 last ¶) ──────────────────────────
export type WorkMode = 'assess' | 'stabilize' | 'extract' | 'observe' | 'recover' | 'restore';
export const WORK_MODES: readonly WorkMode[] = ['assess', 'stabilize', 'extract', 'observe', 'recover', 'restore'];
export const SITE_WORK_RANGE_M = 3_000;
export const BEAT_MIN_MS = 800;                // ≤ 1 Hz (a little jitter allowed)
export const BEAT_CREDIT_MAX_MS = 2_000;       // ≤ 2 s credited per accepted beat
export const BEAT_PAUSE_MS = 3_000;            // a longer gap pauses: the next beat credits nothing
export const ASSESS_MS = 8_000;
export const STABILIZE_MS = 10_000;
export const EXTRACT_BASE_MS = 12_000;
export const EXTRACT_PER_U_MS = 300;
export const EXTRACT_MAX_MS = 30_000;
export const OBSERVE_MS = 10_000;              // the mystery's second observation
export const RECOVER_MS = 12_000;              // the mystery's wreck recorder
export const RESTORE_MS = 15_000;              // the mystery's restoration
export const STABILIZE_FAMILY = 'crystal';     // a stabilisation charge: 2 u of crystal from the hold
export const STABILIZE_UNITS = 2;
export const STABILIZE_DROP = 25;
/** Credit for one beat: 0 for the first beat of a step or after a pause; never more than 2 s. */
export function workCredit(lastBeatMs: number, nowMs: number): number {
  if (lastBeatMs <= 0) return 0;
  const gap = nowMs - lastBeatMs;
  if (gap <= 0 || gap > BEAT_PAUSE_MS) return 0;
  return Math.min(gap, BEAT_CREDIT_MAX_MS);
}
/** A beat that arrives sooner than the 1 Hz cadence is ignored (no credit, the clock does not move). */
export function beatTooSoon(lastBeatMs: number, nowMs: number): boolean { return lastBeatMs > 0 && nowMs - lastBeatMs < BEAT_MIN_MS; }
export function needMs(mode: WorkMode, units: number): number {
  if (mode === 'assess') return ASSESS_MS;
  if (mode === 'stabilize') return STABILIZE_MS;
  if (mode === 'extract') return Math.min(EXTRACT_MAX_MS, EXTRACT_BASE_MS + EXTRACT_PER_U_MS * Math.max(0, units));
  if (mode === 'observe') return OBSERVE_MS;
  if (mode === 'recover') return RECOVER_MS;
  return RESTORE_MS;
}
export function relayOutcome(hazard: number): 'intact' | 'damaged' { return hazard >= DAMAGE_AT ? 'damaged' : 'intact'; }
/** A vault after one extraction of `units`: the strain may cross the seeded threshold — the vault collapses and
 *  what is left is lost (declared, never respawned). */
export function vaultAfter(stock: number, hazard: number, collapseAt: number, units: number): { stock: number; hazard: number; collapsed: boolean; lost: number } {
  const left = Math.max(0, stock - units);
  const h = Math.min(100, hazard + VAULT_STRAIN_PER_U * units);
  if (h >= collapseAt && left > 0) return { stock: 0, hazard: h, collapsed: true, lost: left };
  return { stock: left, hazard: h, collapsed: false, lost: 0 };
}

// ── THE RESEARCH POSTS AND THE FUNDED TENDER ───────────────────────────────
export interface ResearchPostSpec { key: string; systemId: string; name: string; ring: readonly string[] }
/** Three survey desks. A ring is the post's own system + its three nearest charted systems (galaxy distance). */
export const RESEARCH_POSTS: readonly ResearchPostSpec[] = [
  { key: 'sys0:st3', systemId: 'sys0', name: 'Farlight Terminal survey desk', ring: ['sys0', 'sys97', 'sys74', 'sys78'] },
  { key: 'sys4:st0', systemId: 'sys4', name: 'Ostanth Waypoint survey desk', ring: ['sys4', 'sys8', 'sys12', 'sys16'] },
  { key: 'sys95:st0', systemId: 'sys95', name: 'Ilyeste Prime Waypoint survey desk', ring: ['sys95', 'sys99', 'sys91', 'sys68'] },
];
export function researchPostOf(stationKey: string): ResearchPostSpec | null { return RESEARCH_POSTS.find((p) => p.key === stationKey) ?? null; }
/** A post's wallet is a deterministic identity in its own range (firms 0x5f1a…, banks 0xba4c…, guild vaults 0x6a11…). */
export function researchPostBits(index: number): bigint { return 0x5e5e_0000_0000n + BigInt(index + 1); }
export const GRANT_SOV_PER_H = 40;             // the platform's exploration programme — explicit quota, explicit source
export const POST_BUDGET_CAP = 2_400;          // the programme tops a post up to this, never past it
export const POST_TENDERS_MAX = 3;
export const POST_CHANNELS = 4;                // one per ring system
export const CHANNEL_DRIFT_S = 12 * 3_600;     // a lit channel drifts dark after this (the relays drift)
export const TENDER_REWARD_SOV_PER_TIER = 120;
export const TENDER_DELIVERY_GRACE_S = 2 * 3_600;
export const TENDER_ACCEPT_HOLD_S = 45 * 60;   // an accepted tender with no lease on its site reopens after this
export const TENDERS_PER_PILOT = 2;
export const LATE_HANDIN_PCT = 30;
export function tenderReward(tier: number): number { return TENDER_REWARD_SOV_PER_TIER * Math.max(1, Math.min(3, tier)); }
export function tenderDeadlineMs(recoverMs: number): number { return recoverMs + TENDER_DELIVERY_GRACE_S * 1000; }
/** The escrow a delivery releases: intact pays whole; damaged pays 60 %, the rest returns to the post. */
export function tenderPayout(escrow: number, outcome: 'intact' | 'damaged'): { pilot: number; post: number } {
  if (outcome === 'intact') return { pilot: escrow, post: 0 };
  const pilot = Math.floor(escrow * DAMAGED_PAY_PCT / 100);
  return { pilot, post: escrow - pilot };
}
/** The ring systems a post's lit channels cover (channel i covers ring[i]). */
export function censusSystems(post: ResearchPostSpec, channelsLit: number): string[] { return post.ring.slice(0, Math.max(0, Math.min(POST_CHANNELS, channelsLit))); }

// ── TYPED INTEL LICENCES (the report §20.4) ────────────────────────────────
export type IntelScope = 'hazard' | 'assessment' | 'coords';
export const INTEL_SCOPES: readonly IntelScope[] = ['hazard', 'assessment', 'coords'];
export const INTEL_SCOPE_LABEL: Record<IntelScope, string> = {
  hazard: 'coarse hazard report', assessment: 'detailed site assessment', coords: 'exact coordinates',
};
export const INTEL_OFFERS_MAX = 8;
export const INTEL_LICENCES_MAX = 8;
export const INTEL_PRICE_MIN = 1;
export const INTEL_PRICE_MAX = 10_000;
/** What the seller must hold to offer a scope. */
export function scopeEvidence(scope: IntelScope, f: { round: number; stage: SiteStage | ''; hasBand: boolean }): boolean {
  if (scope === 'coords') return f.round >= 3;
  if (scope === 'assessment') return f.stage === 'assessed';
  return f.stage === 'assessed' || f.hasBand;
}

// ── THE EXPEDITION LOG ─────────────────────────────────────────────────────
export const LOG_MAX = 120;                    // per pilot, oldest pruned
export const LOG_PUBLIC_DELAY_S = 3_600;       // a consented entry reaches the public log an hour late, the DAY only
export function logDay(ms: number): string { return new Date(ms).toISOString().slice(0, 10); }

// ── THE SILENT RELAY (the numbers a pilot can see; the words and places are the server's) ──
export const MYSTERY_KEY = 'silent_relay';
export const MYSTERY_RELAY_KEY = 'm:relay';
export const MYSTERY_WRECK_KEY = 'm:wreck';
export const RESTORE_UNITS = 6;
export const RESTORE_PAY_SOV = 300;
export const RELAY_SCAN_COOLDOWN_S = 3;        // the public aid: the scanner recharges in 3 s in the Commons Reach
export const PRIVATE_ASSESS_PCT = 50;          // the private access: the restorer's assessments there take half the time
export type RelayChoice = 'public' | 'private';
/** The scanner cooldown a pilot gets in a system, given the relay's restoration. */
export function scanCooldownS(systemId: string, choice: string, base: number): number {
  return systemId === 'sys0' && choice === 'public' ? RELAY_SCAN_COOLDOWN_S : base;
}
/** The assessment time for a pilot, given the relay's restoration. */
export function assessMsFor(systemId: string, choice: string, restorer: boolean): number {
  return systemId === 'sys0' && choice === 'private' && restorer ? Math.round(ASSESS_MS * PRIVATE_ASSESS_PCT / 100) : ASSESS_MS;
}
